import { ref, type Ref } from "vue";
import { Facade } from "@/drawing";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import {
  projectWorldBbox,
  useViewSyncedCompute,
  type ScreenRect,
} from "@/composable/usePlanFrameProjection";
import type { AnalysisGroupStatus } from "@/utils/plan-analysis/types";

/**
 * Cadre projeté à l'écran (pixels CSS du canvas) — celui d'un groupe
 * d'analyse, ou celui d'une observation (`tone: "observation"`, bleu, repéré
 * par sa lettre : cf. `useObservationFrames`).
 */
export interface AnalysisFrameRect extends ScreenRect {
  id: string;
  /** N° de badge (rang du groupe dans l'ordre d'analyse). */
  number: number;
  /** Libellé du badge à la place du n° (lettre d'une observation). */
  label?: string;
  /** Cadre d'une observation réelle : bleu, sans les états d'un groupe. */
  tone?: "observation";
  status: AnalysisGroupStatus;
  isCurrent: boolean;
  /** Groupe créé à la main (couleur distincte des détections). */
  manual: boolean;
  /**
   * Mode association de l'onglet Parties (cadres d'observations seulement) :
   * rattachée à la partie sélectionnée ? Null hors mode et pour un groupe.
   */
  assigned: boolean | null;
}

/** Marge visuelle autour de la bbox du groupe (px écran). */
const FRAME_PADDING_PX = 6;

/** Contour d'une vignette détectée, projeté à l'écran. */
export interface VignetteFrameRect extends ScreenRect {
  id: string;
  name: string | null;
}

/**
 * Projection des cadres d'analyse en coordonnées écran — recalculée à chaque
 * changement de vue et à chaque mutation de l'analyse (cf.
 * `useViewSyncedCompute`). Rien n'est dessiné dans le moteur ni dans la
 * base : la surcouche est du pur HTML au-dessus du canvas.
 */
export function useAnalysisFrames(): {
  frames: Ref<AnalysisFrameRect[]>;
  vignetteFrames: Ref<VignetteFrameRect[]>;
} {
  const state = usePlanAnalysisState();
  const frames = ref<AnalysisFrameRect[]>([]);
  const vignetteFrames = ref<VignetteFrameRect[]>([]);

  const compute = (): void => {
    if (!Facade.isInitialized || !state.hasAnalysis) {
      frames.value = [];
      vignetteFrames.value = [];
      return;
    }
    // Contours des vignettes : montrer COMMENT la feuille a été découpée.
    vignetteFrames.value = state.vignettes.map((v) => ({
      id: v.id,
      name: v.name,
      ...projectWorldBbox(v.bbox),
    }));
    const out: AnalysisFrameRect[] = [];
    for (const group of state.groups) {
      const status = state.statusOf(group.id);
      // Un groupe ignoré reste listé (grisé) dans le volet, mais son cadre
      // disparaît du plan.
      if (status === "ignored") continue;
      out.push({
        id: group.id,
        number: state.groupNumber(group.id),
        status,
        isCurrent: state.currentGroupId === group.id,
        manual: group.manual === true,
        assigned: null,
        ...projectWorldBbox(group.bbox, FRAME_PADDING_PX),
      });
    }
    frames.value = out;
  };

  useViewSyncedCompute(compute, [
    (): unknown => state.groups,
    (): unknown => state.statuses,
    // Un groupe validé dont l'observation a disparu redevient « à valider ».
    (): unknown => state.knownObservationLocalIds,
    (): unknown => state.currentGroupId,
    (): unknown => state.statusFilter,
    (): unknown => state.vignettes,
    (): unknown => state.edits,
    (): unknown => state.aiSuggestions,
  ]);

  return { frames, vignetteFrames };
}
